import React from 'react';
import AppLogo from '@/components/ui/AppLogo';

const metrics = [
  { label: 'Health Score', value: '87', unit: '/100', color: 'text-[#00F5A0]', bg: 'bg-[#00F5A0]/10' },
  { label: 'HRV (RMSSD)', value: '68', unit: 'ms', color: 'text-[#4CC9F0]', bg: 'bg-[#4CC9F0]/10' },
  { label: 'Recovery', value: '92', unit: '%', color: 'text-[#6C5CE7]', bg: 'bg-[#6C5CE7]/10' },
  { label: 'Streak', value: '14', unit: 'days', color: 'text-[#FFB347]', bg: 'bg-[#FFB347]/10' },
];

const features = [
  'Wearable-synced recovery scoring',
  'AI-powered nutrition analysis',
  'Peptide & protocol management',
  'Expert advisor supervision',
  'Gamified longevity milestones',
];

export default function AuthBrandPanel() {
  return (
    <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] flex-col relative overflow-hidden bg-gradient-to-br from-[hsl(220,50%,3%)] via-[hsl(250,40%,7%)] to-[hsl(220,50%,3%)]">
      {/* Glow orbs */}
      <div className="absolute top-[-120px] left-[-80px] w-[480px] h-[480px] rounded-full bg-[#6C5CE7]/10 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-[-80px] right-[-60px] w-[360px] h-[360px] rounded-full bg-[#4CC9F0]/8 blur-[80px] pointer-events-none" />
      <div className="absolute top-[40%] left-[60%] w-[200px] h-[200px] rounded-full bg-[#00F5A0]/6 blur-[60px] pointer-events-none" />
      <div className="relative z-10 flex flex-col h-full px-10 xl:px-14 py-10">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <AppLogo size={36} />
          <span className="font-display font-semibold text-lg tracking-tight text-[hsl(210,40%,96%)]">
            HolisticAI
            <span className="bg-gradient-to-r from-[#6C5CE7] to-[#00F5A0] bg-clip-text text-transparent">.Health</span>
          </span>
          <span className="text-[10px] font-mono-num font-medium px-1.5 py-0.5 rounded bg-[#6C5CE7]/20 text-[#8B7CFF] border border-[#6C5CE7]/30 tracking-wider uppercase">
            Nexora
          </span>
        </div>

        {/* Hero text */}
        <div className="mt-16 xl:mt-20">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00F5A0]/10 border border-[#00F5A0]/20 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00F5A0] animate-pulse" />
            <span className="text-xs font-medium text-[#00F5A0] tracking-wide">Engineered Longevity Platform</span>
          </div>
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-[1.1] text-[hsl(210,40%,96%)]">
            Optimise every
            <br />
            <span className="bg-gradient-to-r from-[#6C5CE7] to-[#4CC9F0] bg-clip-text text-transparent">
              system in your body.
            </span>
          </h1>
          <p className="mt-5 text-base text-[hsl(215,15%,60%)] leading-relaxed max-w-sm">
            HolisticAI.Health unifies your fitness, nutrition, peptide protocols, wearables, and expert coaching into one precision-engineered daily practice.
          </p>
        </div>

        {/* Live metric chips */}
        <div className="mt-10 grid grid-cols-2 gap-3">
          {metrics?.map((m) => (
            <div
              key={`brand-metric-${m?.label}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border border-[hsl(215,20%,18%)] ${m?.bg}`}
            >
              <div className={`font-mono-num text-2xl font-semibold ${m?.color}`}>
                {m?.value}
                <span className="text-sm font-normal ml-0.5">{m?.unit}</span>
              </div>
              <div className="text-xs text-[hsl(215,15%,55%)] font-medium">{m?.label}</div>
            </div>
          ))}
        </div>

        {/* Feature list */}
        <div className="mt-10 space-y-2.5">
          {features?.map((f) => (
            <div key={`feature-${f}`} className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full bg-[#6C5CE7]/20 border border-[#6C5CE7]/40 flex items-center justify-center flex-shrink-0">
                <svg className="w-2.5 h-2.5 text-[#8B7CFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-sm text-[hsl(215,15%,65%)]">{f}</span>
            </div>
          ))}
        </div>

        {/* Bottom disclaimer */}
        <div className="mt-auto pt-8">
          <p className="text-xs text-[hsl(215,15%,40%)] leading-relaxed border-t border-[hsl(215,20%,14%)] pt-4">
            Wearable and peptide data is informational, not diagnostic. Consult a qualified medical professional before making health decisions.
          </p>
        </div>
      </div>
    </div>
  );
}